/**
 * MeetingEditModal — edit meeting metadata (handoff §3.3).
 *
 * Editable: label, status, scheduled date/time, held start/end, attendees.
 * Sends only the fields the user touched. AM display name is set
 * server-side from the logged-in identity.
 *
 * Date/time inputs are HTML5 datetime-local controls — pragmatic vs a
 * full Tensaw DatePicker because the AM enters precise times in dev/local
 * format anyway. (Flag for the developer if you want the DatePicker swap.)
 */
import { useEffect, useState } from "react";
import type { MeetingDetail } from "@/api/models";
import { usePatchMeeting } from "@/hooks/usePatchMeeting";

export interface MeetingEditModalProps {
  clinicId: number;
  meeting: MeetingDetail;
  onClose: () => void;
}

const STATUSES = ["scheduled", "in_progress", "held", "cancelled"];

/** Convert ISO datetime string ("2026-05-26T14:00:00") to "2026-05-26T14:00"
 * form expected by `<input type="datetime-local">`. */
function toDatetimeLocal(s: string | null | undefined): string {
  if (!s) return "";
  // Strip seconds/zone; the local input accepts "YYYY-MM-DDTHH:mm".
  const m = /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2})/.exec(s);
  return m ? m[1]! : "";
}

function fromDatetimeLocal(s: string): string | null {
  if (!s) return null;
  return `${s}:00`;
}

export function MeetingEditModal({ clinicId, meeting, onClose }: MeetingEditModalProps) {
  const patch = usePatchMeeting(clinicId);

  const [label, setLabel] = useState(meeting.label);
  const [status, setStatus] = useState(meeting.status);
  const [scheduledAt, setScheduledAt] = useState(toDatetimeLocal(meeting.scheduled_at));
  const [heldStart, setHeldStart] = useState(toDatetimeLocal(meeting.held_started_at));
  const [heldEnd, setHeldEnd] = useState(toDatetimeLocal(meeting.held_ended_at));
  const [attendees, setAttendees] = useState(meeting.client_attendees);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const onSave = async () => {
    await patch.mutateAsync({
      meetingId: meeting.meeting_id,
      body: {
        label,
        status,
        scheduled_at: fromDatetimeLocal(scheduledAt),
        held_started_at: fromDatetimeLocal(heldStart),
        held_ended_at: fromDatetimeLocal(heldEnd),
        client_attendees: attendees,
      },
    });
    onClose();
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="meeting-edit-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-lg rounded-lg bg-white shadow-xl">
        <div className="border-b border-border px-5 py-3">
          <h3 id="meeting-edit-title" className="text-base font-semibold">
            Edit meeting
          </h3>
        </div>
        <div className="grid grid-cols-2 gap-3 px-5 py-4">
          <div className="col-span-2">
            <label className="block text-xs font-medium uppercase tracking-wider text-gray-500" htmlFor="meeting-label">
              Label
            </label>
            <input
              id="meeting-label"
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              className="mt-1 w-full rounded border border-border px-2 py-1.5 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium uppercase tracking-wider text-gray-500" htmlFor="meeting-status">
              Status
            </label>
            <select
              id="meeting-status"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="mt-1 w-full rounded border border-border px-2 py-1.5 text-sm"
            >
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s.replace("_", " ")}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium uppercase tracking-wider text-gray-500" htmlFor="meeting-scheduled">
              Scheduled
            </label>
            <input
              id="meeting-scheduled"
              type="datetime-local"
              value={scheduledAt}
              onChange={(e) => setScheduledAt(e.target.value)}
              className="mt-1 w-full rounded border border-border px-2 py-1.5 text-sm tnum"
            />
          </div>
          <div>
            <label className="block text-xs font-medium uppercase tracking-wider text-gray-500" htmlFor="meeting-held-start">
              Held — start
            </label>
            <input
              id="meeting-held-start"
              type="datetime-local"
              value={heldStart}
              onChange={(e) => setHeldStart(e.target.value)}
              className="mt-1 w-full rounded border border-border px-2 py-1.5 text-sm tnum"
            />
          </div>
          <div>
            <label className="block text-xs font-medium uppercase tracking-wider text-gray-500" htmlFor="meeting-held-end">
              Held — end
            </label>
            <input
              id="meeting-held-end"
              type="datetime-local"
              value={heldEnd}
              onChange={(e) => setHeldEnd(e.target.value)}
              className="mt-1 w-full rounded border border-border px-2 py-1.5 text-sm tnum"
            />
          </div>
          <div className="col-span-2">
            <label className="block text-xs font-medium uppercase tracking-wider text-gray-500" htmlFor="meeting-attendees">
              Attendees (client)
            </label>
            <input
              id="meeting-attendees"
              type="text"
              value={attendees}
              onChange={(e) => setAttendees(e.target.value)}
              className="mt-1 w-full rounded border border-border px-2 py-1.5 text-sm"
              placeholder="Dr. Mark Levinson (MD), April Reyes (Office Mgr)"
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 border-t border-border bg-gray-50 px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            disabled={patch.isPending}
            className="rounded-button border border-border bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void onSave()}
            disabled={patch.isPending}
            className="rounded-button bg-brand-primary px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-hover disabled:opacity-60"
          >
            {patch.isPending ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
